type ApiReadinessScope = 'api' | 'full';

export type WaitForApiReadyOptions = {
  apiOrigin?: string;
  scope?: ApiReadinessScope;
  maxAttempts?: number;
  delayMs?: number;
  sleep?: (delayMs: number) => Promise<void>;
};

export function resolveApiOrigin(
  env: Record<string, string | undefined> = typeof process !== 'undefined' ? process.env : {},
): string {
  return (
    env.API_INTERNAL_URL ||
    env.NEXT_PUBLIC_API_URL ||
    'http://localhost:3001'
  ).replace(/\/$/, '');
}

export async function checkApiReadiness(
  apiOrigin: string,
  scope: ApiReadinessScope = 'api',
): Promise<boolean> {
  const path = scope === 'full' ? '/api/health/ready' : '/api/health/ready/api';
  try {
    const res = await fetch(`${apiOrigin}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { status?: string };
    return body.status === 'ok';
  } catch {
    return false;
  }
}

export async function waitForApiReady(options: WaitForApiReadyOptions = {}): Promise<boolean> {
  const apiOrigin = options.apiOrigin ?? resolveApiOrigin();
  const scope = options.scope ?? 'api';
  const maxAttempts = options.maxAttempts ?? 60;
  const delayMs = options.delayMs ?? 500;
  const sleep =
    options.sleep ??
    ((delay: number) => new Promise<void>((resolve) => setTimeout(resolve, delay)));

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (await checkApiReadiness(apiOrigin, scope)) {
      return true;
    }
    if (attempt < maxAttempts) {
      await sleep(delayMs);
    }
  }

  return false;
}
